import {
  Award,
  BarChart3,
  Building2,
  Calculator,
  ClipboardCheck,
  Clock,
  Coins,
  FileText,
  Gavel,
  Handshake,
  HardHat,
  History,
  Inbox,
  Network,
  ShieldCheck,
  Upload,
  UserRound,
  Wallet,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { PERMISSIONS } from '@helios/authz';

/**
 * Subject hubs collapse sibling catalog destinations into one rail row with
 * horizontal tabs. The flat CATALOG is unchanged — hubs only decide what the
 * sidebar renders and how those screens share chrome.
 */
export interface SubjectHubTab {
  /** Catalog id when the tab is a real destination (permission + deep link). */
  destinationId?: string;
  /** Override path. Required when there is no catalog destination. */
  to?: string;
  /** Override the catalog label (e.g. Hours instead of Payroll). */
  label?: string;
  icon?: LucideIcon;
  /**
   * Required for path-only tabs (no catalog destination). Fail-closed in
   * `filterHubTabsForPermissions` — a tab without a destination or permission
   * never renders.
   */
  permission?: string;
  /** Additional alternatives required alongside permission for path-only tabs. */
  anyPermissions?: readonly string[];
  /** Exact path match. Default true. */
  end?: boolean;
  /**
   * Extra path prefixes that keep this tab selected. Used when a merged
   * screen still owns detail routes (a lead record under Intake).
   */
  activePaths?: readonly string[];
  /** Destination used when the main tab is not accessible to this role. */
  fallbackDestinationId?: string;
}

/**
 * One step in an end-to-end subject process. Rendered as a dense horizontal
 * rail above hub tabs. A phase may own one tab or several; the first
 * accessible destination is the click target.
 */
export interface SubjectHubPhase {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Catalog destination ids owned by this phase, in process order. */
  destinationIds: readonly string[];
}

export interface SubjectHub {
  id: string;
  label: string;
  eyebrow: string;
  description: string;
  /** Catalog destination that remains on the left rail. */
  railDestinationId: string;
  /** Sidebar / palette label for the rail row. Tabs keep catalog labels. */
  railLabel?: string;
  /**
   * The hub is already mounted by a nested workspace route (Payroll,
   * Accounting, HR). HubAwareOutlet must not wrap those paths a second time.
   */
  ownsNestedLayout?: boolean;
  tabs: readonly SubjectHubTab[];
  /**
   * Optional process rail. When set, the hub lands on the first accessible
   * tab and highlights the phase that owns the open screen. Tabs omitted
   * here (Config) stay reachable without claiming a lifecycle step.
   */
  phases?: readonly SubjectHubPhase[];
  /**
   * Header shortcuts to screens another hub or Settings owns. Unlike tabs they
   * never claim the target path, so the target keeps its own chrome.
   */
  links?: readonly SubjectHubLink[];
}

export interface SubjectHubLink {
  label: string;
  to: string;
  /** Fail-closed: the link renders only when the user holds this permission. */
  permission: string;
}

/** Staff entry to the subcontractor/vendor portal administration (M11). */
export const SUBCONTRACTOR_PORTAL_LINK: SubjectHubLink = {
  label: 'Subcontractor portal',
  to: '/settings/portal',
  permission: PERMISSIONS.PORTAL_CONTACTS_READ,
};

export const SUBJECT_HUBS: readonly SubjectHub[] = [
  {
    id: 'estimations-bids',
    label: 'Estimations & Bids',
    eyebrow: 'Preconstruction',
    description:
      'From the sponsor email to a number you can sign: intake, package, materials, labour, equipment, company cost, then margin.',
    railDestinationId: 'estimates',
    railLabel: 'Estimations & Bids',
    // The first six tabs are the approved primary areas (HubAwareOutlet renders
    // them through EstimatingWorkspaceShell). The remaining tabs are the
    // specialised legacy tools; they stay in the hub so their URLs keep the
    // shared chrome and are reachable from the project workspace.
    tabs: [
      { destinationId: 'estimating-command', label: 'Command Center' },
      {
        destinationId: 'bid-intake',
        label: 'Intake',
        activePaths: ['/estimating/leads'],
      },
      { destinationId: 'estimating-projects', label: 'Projects' },
      { destinationId: 'bid-register', label: 'Bid Register' },
      { destinationId: 'estimating-intelligence', label: 'Intelligence' },
      { destinationId: 'estimating-config', label: 'Settings' },
      { destinationId: 'takeoff', label: 'Package' },
      { destinationId: 'comping', label: 'Vendor cost' },
      { destinationId: 'cost-database', label: 'Cost book' },
      { destinationId: 'labor-plan', label: 'Labour' },
      { destinationId: 'estimating-equipment', label: 'Equipment' },
      {
        destinationId: 'estimates',
        label: 'Company cost',
        activePaths: ['/estimating/pipeline'],
        fallbackDestinationId: 'estimating-pipeline',
      },
      { destinationId: 'bid-assembly', label: 'Bid Assembly' },
      { destinationId: 'bid-counterparties' },
      { destinationId: 'bid-history' },
    ],
    links: [SUBCONTRACTOR_PORTAL_LINK],
    phases: [
      { id: 'intake', label: 'Intake', icon: Inbox, destinationIds: ['bid-intake'] },
      { id: 'package', label: 'Package', icon: FileText, destinationIds: ['takeoff'] },
      {
        id: 'materials',
        label: 'Materials',
        icon: Coins,
        destinationIds: ['comping', 'cost-database'],
      },
      { id: 'labour', label: 'Labour', icon: HardHat, destinationIds: ['labor-plan'] },
      {
        id: 'equipment',
        label: 'Equipment',
        icon: Wrench,
        destinationIds: ['estimating-equipment'],
      },
      { id: 'cost', label: 'Cost', icon: Calculator, destinationIds: ['estimates'] },
      { id: 'bid', label: 'Bid', icon: Gavel, destinationIds: ['bid-assembly'] },
      {
        id: 'record',
        label: 'Record',
        icon: History,
        destinationIds: ['bid-counterparties', 'bid-history'],
      },
    ],
  },
  {
    id: 'project-controls',
    label: 'Project Controls',
    eyebrow: 'Operations',
    description: 'The job from the office: budget, schedule, RFIs, and change.',
    railDestinationId: 'project-overview',
    railLabel: 'Project Controls',
    tabs: [
      { destinationId: 'project-overview', label: 'Overview' },
      { destinationId: 'project-budget', label: 'Budget' },
      { destinationId: 'project-schedule', label: 'Schedule' },
      { destinationId: 'scheduling' },
      { destinationId: 'rfis' },
      { destinationId: 'submittals' },
      { destinationId: 'change-orders' },
      { destinationId: 'portal-selections' },
    ],
    links: [SUBCONTRACTOR_PORTAL_LINK],
  },
  {
    id: 'field-ops',
    label: 'Field Operations',
    eyebrow: 'Operations',
    description: 'Who is on site, what was filed today, and what is still open.',
    railDestinationId: 'field-today',
    railLabel: 'Field Operations',
    tabs: [
      { destinationId: 'field-today' },
      { destinationId: 'field-dispatch' },
      { destinationId: 'field-daily-reports' },
      { destinationId: 'field-tasks' },
      { destinationId: 'field-observations' },
      { destinationId: 'field-rfis' },
    ],
  },
  {
    id: 'financials',
    label: 'Finance',
    eyebrow: 'Finance',
    description: 'Portfolio money, imported masters, billing, retainage, job cost, and the ledger.',
    railDestinationId: 'finance',
    railLabel: 'Finance',
    tabs: [
      { destinationId: 'finance', label: 'Overview' },
      {
        to: '/finance/receivables',
        label: 'Receivables',
        permission: PERMISSIONS.FINANCE_AR_INVOICES_READ,
      },
      {
        to: '/finance/payables',
        label: 'Payables',
        permission: PERMISSIONS.FINANCE_AP_INVOICES_READ,
      },
      { destinationId: 'finance-subjects', label: 'Customers & Vendors' },
      { destinationId: 'finance-analytics', label: 'Analytics' },
      { destinationId: 'accounting-workspace', label: 'QuickBooks' },
      { destinationId: 'quickbooks-reports', label: 'Reports' },
      { destinationId: 'finance-taxes', label: 'Taxes' },
      { destinationId: 'billing-applications' },
      { destinationId: 'billing-collections' },
      { destinationId: 'retainage-register' },
      { destinationId: 'job-cost-export' },
      { destinationId: 'general-ledger' },
      {
        to: '/finance/accounts',
        label: 'Book accounts & statements',
        permission: PERMISSIONS.BANKING_ACCOUNTS_READ,
      },
      { destinationId: 'bank-accounts', label: 'Book accounts' },
      { destinationId: 'bank-transactions', label: 'Transactions' },
      { destinationId: 'bank-spending', label: 'Recorded transaction activity' },
      { destinationId: 'bank-statements', label: 'Statements' },
      { destinationId: 'bank-reconciliation', label: 'Reconciliation' },
      { destinationId: 'accounting-mappings', label: 'Mappings' },
      { destinationId: 'accounting-sync', label: 'Sync & coverage' },
    ],
  },
  {
    id: 'payroll',
    label: 'Payroll',
    eyebrow: 'Finance',
    description:
      'Hours, stored-rate cost, certified payroll, and the wage schedules they are priced against.',
    railDestinationId: 'payroll-exports',
    ownsNestedLayout: true,
    tabs: [
      { destinationId: 'payroll-exports', label: 'Hours', end: true },
      { destinationId: 'payroll-cost', end: false },
      { destinationId: 'certified-payroll', end: false },
      { destinationId: 'wage-schedules' },
    ],
  },
  {
    id: 'accounting',
    label: 'Accounting',
    eyebrow: 'Finance',
    description: 'Balances, the register, reconciliation, and the QuickBooks link.',
    railDestinationId: 'bank-accounts',
    ownsNestedLayout: true,
    tabs: [
      { destinationId: 'bank-accounts', label: 'Accounts', end: true },
      {
        to: '/finance/accounts',
        label: 'Accounts & Statements',
        icon: Handshake,
        permission: PERMISSIONS.BANKING_ACCOUNTS_READ,
      },
      { destinationId: 'bank-transactions', label: 'Register' },
      { destinationId: 'bank-spending', label: 'Monthly costs' },
      { destinationId: 'bank-statements', label: 'Statements' },
      { destinationId: 'bank-reconciliation', label: 'Reconciliation' },
      { destinationId: 'accounting-workspace', label: 'QuickBooks' },
      {
        to: '/finance/subjects',
        label: 'Customers & Vendors',
        icon: Handshake,
        permission: PERMISSIONS.ACCOUNTING_ENTITY_LINKS_READ,
      },
      { destinationId: 'accounting-mappings' },
      { destinationId: 'accounting-sync' },
    ],
  },
  {
    id: 'hr',
    label: 'HR',
    eyebrow: 'People',
    description: 'The employment lifecycle, end to end.',
    railDestinationId: 'hr',
    railLabel: 'HR',
    ownsNestedLayout: true,
    tabs: [
      { destinationId: 'hr', label: 'Overview', end: true },
      {
        to: '/hr/employees',
        label: 'Employees',
        icon: UserRound,
        permission: PERMISSIONS.HR_EMPLOYEES_READ,
        end: false,
      },
      {
        to: '/hr/org-chart',
        label: 'Organization',
        icon: Network,
        permission: PERMISSIONS.HR_EMPLOYEES_READ,
      },
      {
        to: '/hr/onboarding',
        label: 'Onboarding',
        icon: ClipboardCheck,
        permission: PERMISSIONS.HR_EMPLOYEES_READ,
      },
      {
        to: '/hr/compensation',
        label: 'Compensation',
        icon: Wallet,
        permission: PERMISSIONS.HR_COMPENSATION_READ,
      },
      {
        to: '/hr/time',
        label: 'Time',
        icon: Clock,
        permission: PERMISSIONS.HR_TIME_ENTRIES_READ,
      },
      {
        to: '/hr/performance',
        label: 'Performance',
        icon: Award,
        permission: PERMISSIONS.HR_REVIEWS_READ,
      },
      {
        to: '/hr/documents',
        label: 'Documents',
        icon: FileText,
        permission: PERMISSIONS.HR_DOCUMENTS_READ,
      },
      {
        to: '/hr/compliance',
        label: 'Compliance',
        icon: ShieldCheck,
        permission: PERMISSIONS.HR_EMPLOYEES_READ,
      },
      {
        to: '/hr/analytics',
        label: 'Analytics',
        icon: BarChart3,
        permission: PERMISSIONS.HR_EMPLOYEES_READ,
      },
      {
        to: '/hr/imports',
        label: 'Imports',
        icon: Upload,
        permission: PERMISSIONS.HR_IMPORTS_READ,
      },
    ],
  },
  {
    id: 'benefits',
    label: 'Benefits',
    eyebrow: 'People',
    description: 'Plans, enrollment, vendors, costs, claims, compliance and documents.',
    railDestinationId: 'benefits',
    railLabel: 'Benefits',
    ownsNestedLayout: true,
    tabs: [
      { destinationId: 'benefits', label: 'Overview', end: true },
      { destinationId: 'benefits-plans', end: false },
      { destinationId: 'benefits-enrollment' },
      { destinationId: 'benefits-vendors' },
      { destinationId: 'benefits-costs' },
      { destinationId: 'benefits-claims' },
      { destinationId: 'benefits-compliance' },
      { destinationId: 'benefits-documents' },
      { destinationId: 'benefits-reports' },
    ],
  },
  {
    id: 'insurance',
    label: 'Insurance',
    eyebrow: 'Risk',
    description: "The company's own policies, claims, premiums and carriers.",
    railDestinationId: 'insurance',
    railLabel: 'Insurance',
    ownsNestedLayout: true,
    tabs: [
      { destinationId: 'insurance', label: 'Overview', end: true },
      { destinationId: 'insurance-alerts' },
      { destinationId: 'insurance-intake' },
      { destinationId: 'insurance-policies', end: false },
      { destinationId: 'insurance-claims' },
      { destinationId: 'insurance-premiums' },
      { destinationId: 'insurance-coverage' },
      { destinationId: 'insurance-coverage-gaps' },
      { destinationId: 'insurance-carriers' },
      { destinationId: 'insurance-market' },
      { destinationId: 'insurance-documents' },
      { destinationId: 'insurance-profile' },
    ],
  },
  {
    id: 'time-automation',
    label: 'Time & Cost Automation',
    eyebrow: 'People & Cost',
    description: 'Time that captures itself, with every automation off and in dry-run until an Owner decides.',
    railDestinationId: 'time-automation',
    railLabel: 'Time Automation',
    tabs: [
      { destinationId: 'time-automation', label: 'Controls', end: true },
      { destinationId: 'time-automation-monitoring', label: 'Monitoring notice' },
      { destinationId: 'time-automation-provisioning', label: 'Job provisioning' },
      { destinationId: 'time-automation-field-time', label: 'Field time' },
      { destinationId: 'time-automation-reconciliation', label: 'Reconciliation' },
      { destinationId: 'time-automation-field-time-setup', label: 'Field time setup' },
    ],
  },
  {
    id: 'technology',
    label: 'Technology',
    eyebrow: 'IT',
    description:
      'Hardware, software, cameras, network and the Microsoft 365 directory, with keys stored encrypted.',
    railDestinationId: 'it-estate',
    railLabel: 'Technology',
    ownsNestedLayout: true,
    tabs: [
      { destinationId: 'it-estate', label: 'Overview', end: true },
      { destinationId: 'it-hardware', end: false },
      { destinationId: 'it-software', end: false },
      { destinationId: 'it-cameras', end: false },
      { destinationId: 'it-devices' },
      { destinationId: 'it-licenses' },
      { destinationId: 'it-directory' },
      { destinationId: 'it-network' },
      { destinationId: 'it-tenant-config' },
      { destinationId: 'it-import' },
    ],
  },
  {
    id: 'sales-pipeline',
    label: 'Sales Pipeline',
    eyebrow: 'Business Development',
    description: 'Prospects, touchpoints, tasks and introductions before the bid.',
    railDestinationId: 'sales-pipeline',
    railLabel: 'Sales Pipeline',
    ownsNestedLayout: true,
    tabs: [
      { destinationId: 'sales-pipeline', label: 'Pipeline', end: true },
      { destinationId: 'sales-prospects', end: false },
      { destinationId: 'sales-activity' },
      { destinationId: 'sales-reports' },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    eyebrow: 'Sales & Vendors',
    description:
      'Fact-checked posts, second-person approval, and publishing to Instagram, Facebook and the website feed.',
    railDestinationId: 'marketing',
    railLabel: 'Marketing',
    ownsNestedLayout: true,
    tabs: [
      { destinationId: 'marketing', label: 'Overview', end: true },
      { destinationId: 'marketing-content', end: false },
      { destinationId: 'marketing-autopilot' },
      { destinationId: 'marketing-channels' },
      { destinationId: 'marketing-engagement' },
      { destinationId: 'marketing-brand-studio' },
    ],
  },
  {
    // Absorbed the standalone Client Accounts page and the Vendors hub: the
    // native client and vendor workflows are tabs here, on the same records,
    // grants and audit trail. Old /clients and /vendors URLs redirect.
    id: 'unified-crm',
    label: 'CRM',
    eyebrow: 'Contacts & companies',
    description:
      'Complete contact profiles, communication history and relationship follow-through.',
    railDestinationId: 'unified-crm',
    tabs: [
      {
        destinationId: 'unified-crm',
        label: 'Contacts',
        end: true,
        activePaths: [
          '/crm/directory',
          '/crm/identities',
          '/crm/company',
          '/crm/contact',
          '/crm/vendor',
          '/crm/resolve',
          '/crm/import',
        ],
      },
      { to: '/crm/follow-ups', label: 'Follow-ups', icon: Clock, permission: 'crm:unified:read' },
      { to: '/crm/reports', label: 'Reports', icon: BarChart3, permission: 'crm:unified:read' },
      // Native-only readers retain their existing entry points. CRM readers use Tools.
      { destinationId: 'crm-client-accounts', label: 'Client accounts', end: false },
      { destinationId: 'vendors', label: 'Vendor records', activePaths: ['/crm/vendors/ingest'] },
    ],
    links: [{ label: 'Settings', to: '/crm/settings', permission: 'crm:unified:read' }],
  },
  {
    id: 'competitors',
    label: 'Competitors',
    eyebrow: 'Market',
    description: 'Who you bid against: the record, what they are doing, and what to act on.',
    railDestinationId: 'competitors',
    railLabel: 'Competitors',
    tabs: [
      { destinationId: 'competitors', label: 'Overview', end: true },
      { destinationId: 'competitor-signals' },
      { destinationId: 'competitor-news' },
      { destinationId: 'competitor-alerts' },
    ],
  },
  {
    id: 'legal',
    label: 'Legal',
    eyebrow: 'Legal',
    description:
      'Matters, counsel, the corporate structure and the contracts everything else runs on.',
    railDestinationId: 'legal-overview',
    railLabel: 'Legal',
    tabs: [
      { destinationId: 'legal-overview', label: 'Overview', end: true },
      { destinationId: 'legal-matters', end: false },
      { destinationId: 'legal-counsel', end: false },
      { destinationId: 'legal-invoices' },
      { destinationId: 'legal-calendar' },
      { destinationId: 'legal-risk' },
      { destinationId: 'legal-reports' },
      { destinationId: 'legal-entities', end: false },
      { destinationId: 'contracts', end: false },
    ],
  },
  {
    id: 'ai-agents',
    label: 'AI Agents',
    eyebrow: 'Technology',
    description: 'The agent fleet and each advisor workspace.',
    railDestinationId: 'ai-agents',
    tabs: [
      { destinationId: 'ai-agents', label: 'Fleet', end: true },
      { destinationId: 'agent-detail', label: 'Executive Advisor' },
      { destinationId: 'finance-agent-detail', label: 'Finance' },
      { destinationId: 'scheduling-agent-detail', label: 'Scheduling' },
      { destinationId: 'procurement-agent-detail', label: 'Procurement' },
      { destinationId: 'safety-agent-detail', label: 'Safety' },
      { destinationId: 'compliance-agent-detail', label: 'Compliance' },
      { destinationId: 'estimating-agent-detail', label: 'Estimating' },
      { destinationId: 'qa-qc-agent-detail', label: 'QA/QC' },
      { destinationId: 'hr-workforce-agent-detail', label: 'Workforce' },
      { destinationId: 'equipment-agent-detail', label: 'Equipment' },
      { destinationId: 'market-intelligence-agent-detail', label: 'Market' },
      { destinationId: 'growth-ma-agent-detail', label: 'Growth' },
    ],
  },
];

const HUBS_BY_ID = new Map(SUBJECT_HUBS.map((hub) => [hub.id, hub]));
const HUBS_BY_RAIL_ID = new Map(SUBJECT_HUBS.map((hub) => [hub.railDestinationId, hub]));

const HUB_TAB_DESTINATION_IDS = new Set(
  SUBJECT_HUBS.flatMap((hub) =>
    hub.tabs
      .map((tab) => tab.destinationId)
      .filter((id): id is string => Boolean(id) && id !== hub.railDestinationId),
  ),
);

export function findHubById(id: string): SubjectHub | undefined {
  return HUBS_BY_ID.get(id);
}

export function requireHub(id: string): SubjectHub {
  const hub = findHubById(id);
  if (!hub) {
    throw new Error(`Subject hub "${id}" is missing from SUBJECT_HUBS`);
  }
  return hub;
}

export function findHubByRailDestinationId(id: string): SubjectHub | undefined {
  return HUBS_BY_RAIL_ID.get(id);
}

export function isHubTabDestinationId(id: string): boolean {
  return HUB_TAB_DESTINATION_IDS.has(id);
}

/**
 * Catalog screens folded into Intake or Company cost. They stay routeable
 * (the lead record, the estimating-run console) and are not their own menu.
 */
const COLLAPSED_NAV_DESTINATION_IDS = new Set<string>([
  'lead-funnel',
  'estimating-pipeline',
  'vendors-catalog-ingest',
  'crm-vendor-identities',
]);

export function isCollapsedNavDestination(id: string): boolean {
  return COLLAPSED_NAV_DESTINATION_IDS.has(id);
}

export function isHubRailDestinationId(id: string): boolean {
  return HUBS_BY_RAIL_ID.has(id);
}

export function hubTabDestinationIds(hub: SubjectHub): string[] {
  return hub.tabs.map((tab) => tab.destinationId).filter((id): id is string => Boolean(id));
}

export function allHubTabDestinationIds(): readonly string[] {
  return [...HUB_TAB_DESTINATION_IDS];
}

export function railLabelFor(destinationId: string, fallback: string): string {
  return findHubByRailDestinationId(destinationId)?.railLabel ?? fallback;
}
